import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData, ScoreHeader, ScoreMetric } from '../tokens/types';
import { PdfSection, PdfChip } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenChip } from '../primitives/ScreenPrimitives';
import pdf from '../tokens/pdfStyles';
import { colors, fontFamily, severity, space, typeScale } from '../tokens/designTokens';
import type { Severity } from '../tokens/designTokens';
import { getParameterImportance, IMPORTANCE_META } from '../tokens/riskFramework';

const fmt1 = (n: number | null | undefined): string => (n != null ? n.toFixed(1) : '—');

// AICA score is on a 0–4 scale. Grade buckets and severity bands are pegged
// to integer thirds: A+/A in the top third, B+/B in the middle, C/D below.
const gradeFromScore = (n: number | null | undefined): string => {
    if (n == null) return '—';
    if (n >= 3.6) return 'A+';
    if (n >= 3) return 'A';
    if (n >= 2.4) return 'B+';
    if (n >= 1.8) return 'B';
    if (n >= 1.2) return 'C';
    return 'D';
};

const toneForScore = (s: number | null): Severity => {
    if (s == null) return 'MEDIUM';
    if (s >= 3) return 'LOW';
    if (s >= 2) return 'MEDIUM';
    if (s >= 1) return 'HIGH';
    return 'SEVERE';
};

const toneLabel = (tone: Severity): string => {
    if (tone === 'LOW') return 'Healthy';
    if (tone === 'MEDIUM') return 'Watch';
    if (tone === 'HIGH') return 'Elevated risk';
    return 'Severe risk';
};

const scoredMetrics = (h: ScoreHeader): ScoreMetric[] => h.metrics.filter((m) => m.metricScore != null);
const unscoredMetrics = (h: ScoreHeader): ScoreMetric[] => h.metrics.filter((m) => m.metricScore == null);

const PILLAR_FALLBACK_DESCRIPTION: Record<string, string> = {
    'Revenue Score': 'Top-line strength — absolute scale of revenue and recent growth trajectory.',
    'Stability Score': 'Volatility in revenue and cash inflows across recent quarters.',
    'Customer Concentration Score': 'Dependence on the largest customers — risk if a few accounts dominate revenue.',
    'Counter Party Stability': 'Health of the company’s top customers and vendors based on their own credit signals.',
    'Counter Party Stability Score': 'Health of the company’s top customers and vendors based on their own credit signals.',
    'Liquidity Score': 'Short-term ability to meet obligations from current assets and cash on hand.',
    'Profitability Score': 'Operating margins and net profitability over the most recent reported period.',
    'Working Capital Score': 'Trade receivable / payable cycle and inventory holding efficiency.',
    'Compliance Score': 'Punctuality of statutory filings — GST returns, EPF and tax compliance.',
    'Solvency Score': 'Long-term debt servicing capacity relative to equity and cashflows.',
    'Leverage Score': 'Total debt and gearing relative to net worth and earnings.',
};

const pillarDescription = (h: ScoreHeader): string | undefined => h.tooltipDescription || PILLAR_FALLBACK_DESCRIPTION[h.displayName];

const COMPOSITE_DESCRIPTION =
    'AICA Score is a composite credit grade on a 0–4 scale derived from filings, bureau data and behavioural signals. Higher = stronger credit profile.';

const TONE_HELP: Record<Severity, string> = {
    LOW: 'Healthy zone — score ≥ 3. No immediate action required from a credit perspective.',
    MEDIUM: 'Watch zone — score 2–3. Monitor; signals may indicate emerging risk.',
    HIGH: 'Elevated risk — score 1–2. Material concerns; warrants deeper diligence.',
    SEVERE: 'Severe risk — score < 1. Significant concerns; treat as red flag for credit decisioning.',
};

const isMetricRedundantWithHeader = (h: ScoreHeader): boolean => {
    const scored = scoredMetrics(h);
    if (scored.length !== 1) return false;
    const m = scored[0];
    return (
        m.displayName.trim().toLowerCase() === h.displayName.trim().toLowerCase() &&
        h.headerScore != null &&
        Math.abs((m.metricScore ?? -999) - h.headerScore) < 0.05
    );
};

const weakestDrivers = (h: ScoreHeader, n = 3): ScoreMetric[] => {
    if (isMetricRedundantWithHeader(h)) return [];
    return scoredMetrics(h)
        .slice()
        .sort((a, b) => (a.metricScore ?? 0) - (b.metricScore ?? 0))
        .slice(0, n);
};

// ---------------- PDF ----------------

const PdfPillarCard: React.FC<{ header: ScoreHeader }> = ({ header }) => {
    const tone = toneForScore(header.headerScore);
    const sev = severity[tone];
    const drivers = weakestDrivers(header);
    const scored = scoredMetrics(header).length;
    const total = header.metrics.length;
    const description = pillarDescription(header);
    return (
        <View
            style={{
                flexGrow: 1,
                flexBasis: '47%',
                padding: space.sp3,
                borderRadius: 4,
                backgroundColor: sev.bg,
                borderWidth: 0.5,
                borderColor: sev.fg,
                overflow: 'hidden',
            }}
            wrap
            minPresenceAhead={60}
        >
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <View style={{ flex: 1, marginRight: 6, overflow: 'hidden' }}>
                    <Text style={{ fontSize: typeScale.h4, fontFamily: fontFamily.body, fontWeight: 600, color: colors.ink00 }}>
                        {header.displayName}
                    </Text>
                    {description ? (
                        <Text style={{ fontSize: typeScale.caption, color: colors.ink40, marginTop: 2, lineHeight: 1.4 }}>
                            {description}
                        </Text>
                    ) : null}
                </View>
                <View style={{ alignItems: 'flex-end', flexShrink: 0, minWidth: 50 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 3 }}>
                        <Text style={{ fontSize: 18, fontFamily: fontFamily.mono, fontWeight: 500, color: sev.fg, lineHeight: 1 }}>
                            {fmt1(header.headerScore)}
                        </Text>
                        <Text style={{ fontSize: typeScale.caption, color: colors.ink40 }}>/ 4</Text>
                    </View>
                    <Text
                        style={{
                            fontSize: typeScale.micro,
                            color: sev.fg,
                            letterSpacing: 0.6,
                            textTransform: 'uppercase',
                            fontFamily: fontFamily.body,
                            fontWeight: 600,
                            marginTop: 2,
                        }}
                    >
                        {toneLabel(tone)}
                    </Text>
                </View>
            </View>
            {total > 1 ? (
                <Text style={{ fontSize: typeScale.micro, color: colors.ink40, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                    {scored} of {total} signals scored
                </Text>
            ) : null}
            {drivers.length > 0 ? (
                <View style={{ marginTop: 6, paddingTop: 6, borderTopWidth: 0.5, borderTopColor: sev.fg }}>
                    <Text
                        style={{
                            fontSize: typeScale.micro,
                            color: sev.fg,
                            letterSpacing: 0.6,
                            textTransform: 'uppercase',
                            fontFamily: fontFamily.body,
                            fontWeight: 600,
                            marginBottom: 3,
                        }}
                    >
                        Weakest drivers
                    </Text>
                    {drivers.map((m, i) => {
                        const importance = getParameterImportance(m.displayName);
                        return (
                            <View key={i} style={{ marginBottom: 3, overflow: 'hidden' }}>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', marginRight: 4 }}>
                                        <Text style={{ fontSize: typeScale.bodySm, color: colors.ink10, fontWeight: 500 }}>
                                            {m.displayName}
                                        </Text>
                                        {importance ? (
                                            <View
                                                style={{
                                                    marginLeft: 4,
                                                    paddingHorizontal: 3,
                                                    paddingTop: 1,
                                                    paddingBottom: 2,
                                                    backgroundColor: sev.fg,
                                                    borderRadius: 1.5,
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        fontSize: 6,
                                                        color: sev.bg,
                                                        fontFamily: fontFamily.sansBold,
                                                        letterSpacing: 0.3,
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {IMPORTANCE_META[importance].abbr}
                                                </Text>
                                            </View>
                                        ) : null}
                                    </View>
                                    <Text
                                        style={{
                                            fontSize: typeScale.bodySm,
                                            fontFamily: fontFamily.mono,
                                            fontWeight: 500,
                                            color: sev.fg,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {fmt1(m.metricScore)}
                                    </Text>
                                </View>
                                {m.tooltipDescription ? (
                                    <Text style={{ fontSize: typeScale.micro, color: colors.ink40, lineHeight: 1.4, marginTop: 1 }}>
                                        {m.tooltipDescription.length > 120
                                            ? m.tooltipDescription.slice(0, 119) + '…'
                                            : m.tooltipDescription}
                                    </Text>
                                ) : null}
                            </View>
                        );
                    })}
                </View>
            ) : null}
        </View>
    );
};

const PdfPendingSignals: React.FC<{ headers: ScoreHeader[] }> = ({ headers }) => {
    const groups = headers.map((h) => ({ name: h.displayName, pending: unscoredMetrics(h) })).filter((g) => g.pending.length > 0);
    if (groups.length === 0) return null;
    return (
        <View style={{ marginTop: space.sp3 }} minPresenceAhead={50}>
            <View style={pdf.divider} />
            <Text
                style={{
                    fontSize: typeScale.micro,
                    color: colors.ink40,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    fontFamily: fontFamily.body,
                    fontWeight: 600,
                    marginBottom: 4,
                }}
                wrap={false}
            >
                Pending signals
            </Text>
            {groups.map((g) => (
                <View key={g.name} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginBottom: 3 }}>
                    <Text style={{ fontSize: typeScale.caption, color: colors.ink40, width: 100 }}>{g.name}</Text>
                    {g.pending.map((m) => (
                        <PdfChip key={m.displayName} tone="muted">
                            {m.displayName}
                        </PdfChip>
                    ))}
                </View>
            ))}
        </View>
    );
};

const PdfScore: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const s = data.score;
    if (!s) return null;
    const grade = gradeFromScore(s.companyScore);
    const headlineTone = toneForScore(s.companyScore);
    const headlineSev = severity[headlineTone];
    return (
        <PdfSection kicker="AICA grade" title="Credit intelligence score" index={index} breakBefore={breakBefore}>
            {/* Composite score — full-width banner on top. The score / grade / tone /
                description sit on a single horizontal axis so the visual hierarchy is
                "headline number → meaning → context", and the pillar cards line up
                in a clean 2x2 grid below regardless of how many headers we have. */}
            <View
                style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: space.sp4,
                    padding: space.sp3,
                    borderRadius: 4,
                    backgroundColor: colors.accentSoft,
                }}
                wrap={false}
            >
                <View style={{ flexShrink: 0, paddingBottom: 6 }}>
                    <Text
                        style={{
                            fontSize: typeScale.micro,
                            color: colors.accentDeep,
                            letterSpacing: 0.8,
                            textTransform: 'uppercase',
                            fontFamily: fontFamily.body,
                            fontWeight: 600,
                        }}
                    >
                        Composite score
                    </Text>
                    {/* 42pt Geist Mono digits with rounded bottoms ("2", "0", "8")
                        extend ~8pt below the baseline. lineHeight 1.3 gives ~12.6pt
                        descent room inside the line box; paddingBottom on the parent
                        reserves the remaining visual gap so the next sibling (pillar
                        grid) can't overlap the glyph bottoms. baseline alignment is
                        replaced with flex-end so "/ 4" sits at the visual bottom of
                        the digit row without depending on react-pdf's fragile
                        baseline calculation. */}
                    <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 5, marginTop: 2 }}>
                        <Text
                            style={{
                                fontSize: 42,
                                color: colors.accentDeep,
                                fontFamily: fontFamily.mono,
                                fontWeight: 500,
                                lineHeight: 1.3,
                            }}
                        >
                            {fmt1(s.companyScore)}
                        </Text>
                        <Text style={{ fontSize: typeScale.caption, color: colors.ink40, marginBottom: 6 }}>/ 4</Text>
                    </View>
                </View>
                <View style={{ flexShrink: 0, paddingLeft: space.sp3, borderLeftWidth: 0.5, borderLeftColor: colors.rule }}>
                    <Text
                        style={{
                            fontSize: typeScale.micro,
                            color: colors.ink40,
                            letterSpacing: 0.8,
                            textTransform: 'uppercase',
                        }}
                    >
                        Grade
                    </Text>
                    <Text style={{ fontSize: typeScale.displayH3, fontFamily: fontFamily.display, color: colors.ink00, lineHeight: 1.05 }}>
                        {grade}
                    </Text>
                    <Text
                        style={{
                            fontSize: typeScale.micro,
                            color: headlineSev.fg,
                            marginTop: 4,
                            fontFamily: fontFamily.body,
                            fontWeight: 600,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                        }}
                    >
                        {toneLabel(headlineTone)}
                    </Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: typeScale.micro, color: colors.ink40, lineHeight: 1.45 }}>{COMPOSITE_DESCRIPTION}</Text>
                    <Text style={{ fontSize: typeScale.micro, color: headlineSev.fg, marginTop: 4, lineHeight: 1.45, fontWeight: 500 }}>
                        {TONE_HELP[headlineTone]}
                    </Text>
                </View>
            </View>
            {/* Pillar grid — 2-up rows. Skill rule 2: explicit row pairs, never flexWrap. */}
            <View style={{ flexDirection: 'column', gap: space.sp2, marginTop: space.sp3 }}>
                {(() => {
                    const pairs: Array<[(typeof s.headers)[number] | undefined, (typeof s.headers)[number] | undefined]> = [];
                    for (let i = 0; i < s.headers.length; i += 2) pairs.push([s.headers[i], s.headers[i + 1]]);
                    return pairs.map((row, ri) => (
                        <View key={ri} style={{ flexDirection: 'row', gap: space.sp2 }} wrap={false}>
                            {row.map((h, ci) =>
                                h ? (
                                    <View key={ci} style={{ flex: 1 }}>
                                        <PdfPillarCard header={h} />
                                    </View>
                                ) : (
                                    <View key={ci} style={{ flex: 1 }} />
                                ),
                            )}
                        </View>
                    ));
                })()}
            </View>
            {/* Pending Signals block removed per design feedback. The
                `PdfPendingSignals` component and the unscoredMetrics helper
                are kept in source so the block can be re-enabled later by
                restoring this render line. */}
            <PdfScoreLegend />
        </PdfSection>
    );
};

// ── Legend block ────────────────────────────────────────────────
//
// Renders below the pillar grid. Two purposes:
// 1. Explain the grade letters (A+/A/B+/B/C/D) and severity zones (LOW /
//    MEDIUM / HIGH / SEVERE) so a first-time reader can decode the score card
//    without flipping back to a glossary.
// 2. Fill the empty lower portion of the Score page when the pillar grid is
//    short — previously the page ended mid-card and looked sparse.
//
// Styled as a 2-column block (grade scale on left, severity zones on right)
// to mirror the way the score card surfaces them above.
const GRADE_LEGEND: Array<{ letter: string; range: string; meaning: string }> = [
    { letter: 'A+', range: '≥ 4.5', meaning: 'Strongest credit profile' },
    { letter: 'A', range: '3.75 – 4.49', meaning: 'Strong, low concern' },
    { letter: 'B+', range: '3.00 – 3.74', meaning: 'Solid, monitor periodically' },
    { letter: 'B', range: '2.25 – 2.99', meaning: 'Cautious, deeper diligence advised' },
    { letter: 'C', range: '1.50 – 2.24', meaning: 'At-risk, material concerns' },
    { letter: 'D', range: '< 1.50', meaning: 'Severe, treat as red flag' },
];

const ZONE_LEGEND: Array<{ tone: Severity; label: string; range: string; meaning: string }> = [
    { tone: 'LOW', label: 'Healthy', range: 'score ≥ 3.5', meaning: 'No immediate action' },
    { tone: 'MEDIUM', label: 'Watch', range: '2.5 – 3.5', meaning: 'Emerging risk; monitor' },
    { tone: 'HIGH', label: 'Elevated', range: '1.5 – 2.5', meaning: 'Material concerns' },
    { tone: 'SEVERE', label: 'Severe', range: '< 1.5', meaning: 'Red flag for credit' },
];

const PdfScoreLegend: React.FC = () => (
    <View style={{ marginTop: space.md }} wrap={false}>
        <View style={pdf.divider} />
        <Text
            style={{
                fontSize: typeScale.micro,
                color: colors.ink40,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                fontFamily: fontFamily.body,
                fontWeight: 600,
                marginBottom: 8,
            }}
        >
            How to read this score
        </Text>
        <View style={{ flexDirection: 'row', gap: 16 }}>
            {/* Grade scale (left column) */}
            <View style={{ flex: 1 }}>
                <Text
                    style={{
                        fontSize: typeScale.micro,
                        color: colors.ink00,
                        fontFamily: fontFamily.body,
                        fontWeight: 600,
                        marginBottom: 4,
                    }}
                >
                    Grade scale
                </Text>
                {GRADE_LEGEND.map((g) => (
                    <View
                        key={g.letter}
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingVertical: 3,
                            borderBottomWidth: 0.5,
                            borderBottomColor: colors.ruleSoft,
                        }}
                    >
                        <Text
                            style={{
                                width: 28,
                                fontSize: typeScale.caption,
                                fontFamily: fontFamily.body,
                                fontWeight: 700,
                                color: colors.ink00,
                            }}
                        >
                            {g.letter}
                        </Text>
                        <Text style={{ width: 76, fontSize: typeScale.micro, fontFamily: fontFamily.mono, color: colors.ink20 }}>
                            {g.range}
                        </Text>
                        <Text style={{ flex: 1, fontSize: typeScale.micro, color: colors.ink40, lineHeight: 1.4 }}>{g.meaning}</Text>
                    </View>
                ))}
            </View>
            {/* Severity zones (right column) */}
            <View style={{ flex: 1 }}>
                <Text
                    style={{
                        fontSize: typeScale.micro,
                        color: colors.ink00,
                        fontFamily: fontFamily.body,
                        fontWeight: 600,
                        marginBottom: 4,
                    }}
                >
                    Severity zones
                </Text>
                {ZONE_LEGEND.map((z) => {
                    const sev = severity[z.tone];
                    return (
                        <View
                            key={z.tone}
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                paddingVertical: 3,
                                borderBottomWidth: 0.5,
                                borderBottomColor: colors.ruleSoft,
                            }}
                        >
                            <View
                                style={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: 3.5,
                                    backgroundColor: sev.fg,
                                    marginRight: 6,
                                }}
                            />
                            <Text
                                style={{
                                    width: 56,
                                    fontSize: typeScale.caption,
                                    fontFamily: fontFamily.body,
                                    fontWeight: 700,
                                    color: colors.ink00,
                                }}
                            >
                                {z.label}
                            </Text>
                            <Text style={{ width: 70, fontSize: typeScale.micro, fontFamily: fontFamily.mono, color: colors.ink20 }}>
                                {z.range}
                            </Text>
                            <Text style={{ flex: 1, fontSize: typeScale.micro, color: colors.ink40, lineHeight: 1.4 }}>{z.meaning}</Text>
                        </View>
                    );
                })}
            </View>
        </View>
        <Text
            style={{
                fontSize: typeScale.micro,
                color: colors.ink40,
                marginTop: 8,
                lineHeight: 1.5,
            }}
        >
            Each pillar above carries an importance weight (Critical / High / Medium / Low). The composite score is a weighted average
            across pillars, then bucketed into the grade and severity zones above.
        </Text>
    </View>
);

// ---------------- Screen ----------------

const ScreenPillarCard: React.FC<{ header: ScoreHeader }> = ({ header }) => {
    const tone = toneForScore(header.headerScore);
    const sev = severity[tone];
    const drivers = weakestDrivers(header);
    const scored = scoredMetrics(header).length;
    const total = header.metrics.length;
    const description = pillarDescription(header);
    return (
        <div
            style={{
                flexGrow: 1,
                flexBasis: 'calc(50% - 10px)',
                padding: 14,
                borderRadius: 6,
                background: sev.bg,
                border: `1px solid ${sev.fg}`,
                minWidth: 260,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: 600, color: colors.ink00 }}>{header.displayName}</div>
                    {description ? (
                        <div style={{ fontSize: '0.74rem', color: colors.ink20, marginTop: 4, lineHeight: 1.45 }}>{description}</div>
                    ) : null}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 3 }}>
                        <span
                            style={{
                                fontSize: '1.6rem',
                                fontFamily: "'Geist Mono', monospace",
                                fontWeight: 500,
                                color: sev.fg,
                                lineHeight: 1,
                            }}
                        >
                            {fmt1(header.headerScore)}
                        </span>
                        <span style={{ fontSize: '0.72rem', color: colors.ink40 }}>/ 4</span>
                    </div>
                    <div
                        style={{
                            fontSize: '0.64rem',
                            color: sev.fg,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            marginTop: 4,
                        }}
                    >
                        {toneLabel(tone)}
                    </div>
                </div>
            </div>
            {total > 1 ? (
                <div
                    style={{
                        fontSize: '0.62rem',
                        color: colors.ink40,
                        marginTop: 6,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                    }}
                >
                    {scored} of {total} signals scored
                </div>
            ) : null}
            {drivers.length > 0 ? (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${sev.fg}` }}>
                    <div
                        style={{
                            fontSize: '0.62rem',
                            color: sev.fg,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            marginBottom: 4,
                        }}
                    >
                        Weakest drivers
                    </div>
                    {drivers.map((m, i) => {
                        const importance = getParameterImportance(m.displayName);
                        return (
                            <div key={i} style={{ marginBottom: 4 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: '0.8rem', color: colors.ink10, fontWeight: 500 }}>{m.displayName}</span>
                                        {importance ? (
                                            <span
                                                title={`Importance: ${IMPORTANCE_META[importance].label} (${IMPORTANCE_META[importance].weight}%)`}
                                                style={{
                                                    background: sev.fg,
                                                    color: sev.bg,
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    letterSpacing: 0.3,
                                                    padding: '1px 4px',
                                                    borderRadius: 2,
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {IMPORTANCE_META[importance].abbr}
                                            </span>
                                        ) : null}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '0.8rem',
                                            fontFamily: "'Geist Mono', monospace",
                                            fontWeight: 500,
                                            color: sev.fg,
                                            marginLeft: 8,
                                        }}
                                    >
                                        {fmt1(m.metricScore)}
                                    </span>
                                </div>
                                {m.tooltipDescription ? (
                                    <div style={{ fontSize: '0.7rem', color: colors.ink40, lineHeight: 1.4, marginTop: 2 }}>
                                        {m.tooltipDescription}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

const ScreenPendingSignals: React.FC<{ headers: ScoreHeader[] }> = ({ headers }) => {
    const groups = headers.map((h) => ({ name: h.displayName, pending: unscoredMetrics(h) })).filter((g) => g.pending.length > 0);
    if (groups.length === 0) return null;
    return (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.rule}` }}>
            <div
                style={{
                    fontSize: '0.7rem',
                    color: colors.ink40,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    marginBottom: 6,
                }}
            >
                Pending signals
            </div>
            {groups.map((g) => (
                <div key={g.name} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.75rem', color: colors.ink40, width: 130 }}>{g.name}</span>
                    {g.pending.map((m) => (
                        <ScreenChip key={m.displayName} tone="muted">
                            {m.displayName}
                        </ScreenChip>
                    ))}
                </div>
            ))}
        </div>
    );
};

const ScreenScore: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const s = data.score;
    if (!s) return null;
    const grade = gradeFromScore(s.companyScore);
    const headlineTone = toneForScore(s.companyScore);
    const headlineSev = severity[headlineTone];
    return (
        <ScreenSection kicker="AICA grade" title="Credit intelligence score" index={index}>
            {/* Composite — full-width banner on top, pillars in 2-up grid below. Mirrors PdfScore. */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 24,
                    padding: 16,
                    borderRadius: 4,
                    background: colors.accentSoft,
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ flexShrink: 0, paddingBottom: 8 }}>
                    <div
                        style={{
                            fontSize: '0.7rem',
                            color: colors.accentDeep,
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                        }}
                    >
                        Composite score
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginTop: 4 }}>
                        <div
                            style={{
                                fontSize: '3.2rem',
                                color: colors.accentDeep,
                                fontFamily: "'Geist Mono', monospace",
                                fontWeight: 500,
                                lineHeight: 1.3,
                            }}
                        >
                            {fmt1(s.companyScore)}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: colors.ink40, marginBottom: 8 }}>/ 4</div>
                    </div>
                </div>
                <div style={{ flexShrink: 0, paddingLeft: 16, borderLeft: `1px solid ${colors.rule}` }}>
                    <div style={{ fontSize: '0.7rem', color: colors.ink40, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        Grade
                    </div>
                    <div style={{ fontSize: '1.75rem', fontFamily: "'Fraunces', serif", color: colors.ink00, lineHeight: 1.05 }}>
                        {grade}
                    </div>
                    <div
                        style={{
                            fontSize: '0.68rem',
                            color: headlineSev.fg,
                            marginTop: 4,
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                        }}
                    >
                        {toneLabel(headlineTone)}
                    </div>
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: '0.72rem', color: colors.ink40, lineHeight: 1.5 }}>{COMPOSITE_DESCRIPTION}</div>
                    <div style={{ fontSize: '0.72rem', color: headlineSev.fg, marginTop: 4, lineHeight: 1.5, fontWeight: 500 }}>
                        {TONE_HELP[headlineTone]}
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                {(() => {
                    const pairs: Array<[(typeof s.headers)[number] | undefined, (typeof s.headers)[number] | undefined]> = [];
                    for (let i = 0; i < s.headers.length; i += 2) pairs.push([s.headers[i], s.headers[i + 1]]);
                    return pairs.map((row, ri) => (
                        <div key={ri} style={{ display: 'flex', gap: 10 }}>
                            {row.map((h, ci) =>
                                h ? (
                                    <div key={ci} style={{ flex: 1, minWidth: 0 }}>
                                        <ScreenPillarCard header={h} />
                                    </div>
                                ) : (
                                    <div key={ci} style={{ flex: 1 }} />
                                ),
                            )}
                        </div>
                    ));
                })()}
            </div>
            {/* Pending Signals block removed per design feedback — see PdfScore. */}
            <ScreenScoreLegend />
        </ScreenSection>
    );
};

const ScreenScoreLegend: React.FC = () => (
    <div style={{ marginTop: 18, paddingTop: 12, borderTop: `1px solid ${colors.rule}` }}>
        <div
            style={{
                fontSize: '0.7rem',
                color: colors.ink40,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontWeight: 600,
                marginBottom: 10,
            }}
        >
            How to read this score
        </div>
        <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.75rem', color: colors.ink00, fontWeight: 600, marginBottom: 6 }}>Grade scale</div>
                {GRADE_LEGEND.map((g) => (
                    <div
                        key={g.letter}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '5px 0',
                            borderBottom: `0.5px solid ${colors.ruleSoft}`,
                            gap: 8,
                        }}
                    >
                        <span style={{ width: 32, fontSize: '0.85rem', fontWeight: 700, color: colors.ink00 }}>{g.letter}</span>
                        <span
                            style={{
                                width: 100,
                                fontSize: '0.7rem',
                                fontFamily: "'Geist Mono', monospace",
                                color: colors.ink20,
                            }}
                        >
                            {g.range}
                        </span>
                        <span style={{ flex: 1, fontSize: '0.72rem', color: colors.ink40, lineHeight: 1.45 }}>{g.meaning}</span>
                    </div>
                ))}
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.75rem', color: colors.ink00, fontWeight: 600, marginBottom: 6 }}>Severity zones</div>
                {ZONE_LEGEND.map((z) => {
                    const sev = severity[z.tone];
                    return (
                        <div
                            key={z.tone}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '5px 0',
                                borderBottom: `0.5px solid ${colors.ruleSoft}`,
                                gap: 8,
                            }}
                        >
                            <span
                                style={{
                                    width: 9,
                                    height: 9,
                                    borderRadius: '50%',
                                    background: sev.fg,
                                    display: 'inline-block',
                                }}
                            />
                            <span style={{ width: 70, fontSize: '0.85rem', fontWeight: 700, color: colors.ink00 }}>{z.label}</span>
                            <span
                                style={{
                                    width: 90,
                                    fontSize: '0.7rem',
                                    fontFamily: "'Geist Mono', monospace",
                                    color: colors.ink20,
                                }}
                            >
                                {z.range}
                            </span>
                            <span style={{ flex: 1, fontSize: '0.72rem', color: colors.ink40, lineHeight: 1.45 }}>{z.meaning}</span>
                        </div>
                    );
                })}
            </div>
        </div>
        <div style={{ fontSize: '0.72rem', color: colors.ink40, marginTop: 10, lineHeight: 1.55 }}>
            Each pillar above carries an importance weight (Critical / High / Medium / Low). The composite score is a weighted average
            across pillars, then bucketed into the grade and severity zones above.
        </div>
    </div>
);

export const scoreModule: ModuleDefinition = {
    meta: {
        id: 'score',
        title: 'AICA Score',
        description: 'Composite grade with per-pillar risk cards, weakest drivers and pending signals.',
        dataSource: 'Assessee Score API',
    },
    hasData: (d) => !!d.score && typeof d.score.companyScore === 'number',
    Pdf: PdfScore,
    Screen: ScreenScore,
};

export { toneForScore };
